function firstDefined(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '')
}

function normalizeRetryAfterSeconds(value, fallback = null) {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return fallback
  }

  return Math.ceil(parsed)
}

function normalizeRetryableFlag(value, fallback = null) {
  if (typeof value === 'boolean') {
    return value
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'true') return true
    if (normalized === 'false') return false
  }

  return fallback
}

function normalizeErrorCode(value) {
  if (typeof value !== 'string') {
    return value === undefined ? null : value
  }

  const normalized = value.trim().toUpperCase()
  return normalized || null
}

const PREVIEW_BUSINESS_ERROR_MESSAGES = Object.freeze({
  SOURCE_ITEMS_CONTRACT_INVALID: 'Dữ liệu nguồn hóa đơn không đúng contract để dựng preview VAT.',
  SOURCE_ITEM_VAT_MISSING:
    'Thiếu VAT ở một số dòng hàng nguồn đang hoạt động. Vui lòng kiểm tra Product.VAT.',
  SOURCE_ITEM_VAT_INVALID:
    'VAT của hàng hóa nguồn không hợp lệ. Preview chỉ hỗ trợ 0, 5, 8, 10, -1 (KCT). Vui lòng kiểm tra Product.VAT.',
  UNSUPPORTED_VAT_RATE:
    'Cấu hình VAT không hợp lệ. Preview chỉ hỗ trợ 0, 5, 8, 10, -1 (KCT). Vui lòng kiểm tra cấu hình E-Invoice.',
  DISCOUNT_BUCKET_ALLOCATION_FAILED:
    'Không thể phân bổ chiết khấu tổng hợp theo bucket thuế hiện tại.',
  BILL_TYPE_MISMATCH: 'Hóa đơn không thuộc lane e-invoice đã chọn.',
  WHOLESALE_INVOICE_NOT_FOUND: 'Không tìm thấy hóa đơn bán buôn để dựng preview.',
  BILL_MODE_UNSUPPORTED:
    'Kênh nguồn của hóa đơn giao hàng chưa được hỗ trợ để phát hành HĐĐT. Cần normalize Bill.mode hoặc bổ sung policy backend.',
})

export function createClientValidationError(code, message, meta = null) {
  const error = new Error(message)
  error.code = code
  error.meta = meta
  error.isClientValidationError = true
  return error
}

export function createEInvoiceActionError(err, fallbackMessage) {
  if (err?.isClientValidationError) {
    const messageByCode = {
      DRAFT_BATCH_EMPTY_BILL_IDS: 'Cần ít nhất 1 billId hợp lệ để tạo draft',
      DRAFT_BATCH_DUPLICATE_BILL_IDS:
        'Danh sách billIds có phần tử trùng. Vui lòng bỏ trùng trước khi gửi.',
      DRAFT_BATCH_OVERRIDE_OUTSIDE_BILL_IDS:
        'overrides chứa billId không nằm trong billIds đã chọn.',
      DRAFT_BATCH_INVALID_OVERRIDES_SHAPE:
        'Trường overrides phải là object map theo billId hợp lệ.',
      DRAFT_BATCH_EXTRA_TOP_LEVEL_KEYS: 'Payload tạo nháp chỉ được chứa billIds và overrides.',
      DRAFT_STRICT_SCHEMA_INVALID:
        'Payload draft phải dùng đúng canonical override shape 12 keys trước khi gọi API.',
      PREVIEW_CALCULATION_INVALID_PAYLOAD_SHAPE:
        'Payload preview-calculation phải có shape { saleType, invoices: [{ salesInvoiceId, override }] }.',
      PREVIEW_CALCULATION_EMPTY_INVOICES: 'Cần ít nhất 1 invoice hợp lệ để xem trước VAT.',
      EINVOICE_PREVIEW_PDF_CONTRACT_INVALID:
        'Phản hồi preview PDF không hợp lệ từ backend (thiếu pdfBase64).',
      EINVOICE_CANONICAL_ITEM_MONEY_BREAKDOWN_REQUIRED:
        'Phản hồi canonical item thiếu moneyBreakdown bắt buộc. Vui lòng kiểm tra lại dữ liệu backend.',
      EINVOICE_CANONICAL_ITEM_MONEY_BREAKDOWN_FIELD_INVALID:
        'Phản hồi canonical item có moneyBreakdown không hợp lệ. Vui lòng kiểm tra lại dữ liệu backend.',
      EINVOICE_CANONICAL_SUMMARY_REQUIRED:
        'Phản hồi canonical detail thiếu summary bắt buộc. Vui lòng kiểm tra lại dữ liệu backend.',
      EINVOICE_CANONICAL_SUMMARY_FIELD_INVALID:
        'Phản hồi canonical detail có summary không hợp lệ. Vui lòng kiểm tra lại dữ liệu backend.',
      EINVOICE_CANONICAL_CONTEXT_MISSING:
        'Thiếu salesInvoiceId/saleType theo contract canonical. Vui lòng tải lại và thử lại.',
      BILL_TYPES_REQUIRED: 'Cần chọn ít nhất một loại hóa đơn.',
      INVALID_BILL_TYPES: 'Bộ lọc loại hóa đơn không hợp lệ.',
      BILL_TYPE_FILTER_CONFLICT: 'Bộ lọc loại hóa đơn không hợp lệ. Vui lòng tải lại trang.',
    }
    const normalizedMessage = messageByCode[err.code] || err.message || fallbackMessage
    const actionError = new Error(normalizedMessage)
    actionError.originalError = err
    actionError.response = err?.response
    actionError.provider = null
    actionError.code = err.code || null
    actionError.meta = err.meta || null
    actionError.isClientValidationError = true
    actionError.isConfigMissing = false
    actionError.einvoiceBlockerType = null
    actionError.reasonCode = err.code || null
    actionError.fastErrorInfo = null
    actionError.fastErrorCategory = null
    actionError.fastErrorMessage = null
    actionError.fastErrorNote = null
    actionError.httpStatus = null
    actionError.isStaleDraft = false
    actionError.isRetryable = false
    actionError.retryAfterSeconds = null
    return actionError
  }

  const responseData = err?.response?.data || {}
  const providerError = responseData?.error || {}
  const fastErrorInfo =
    providerError?.fastErrorInfo && typeof providerError.fastErrorInfo === 'object'
      ? providerError.fastErrorInfo
      : null
  const providerMessage =
    typeof fastErrorInfo?.message === 'string' ? fastErrorInfo.message.trim() : ''
  const providerNote = typeof fastErrorInfo?.note === 'string' ? fastErrorInfo.note.trim() : ''
  const providerDetail = [providerMessage, providerNote]
    .filter((part, index, parts) => part && parts.indexOf(part) === index)
    .join('. ')
  const message = firstDefined(
    providerDetail || null,
    responseData?.message,
    err?.message,
    fallbackMessage,
  )
  const httpStatus = err?.response?.status || null
  const explicitCode = normalizeErrorCode(
    firstDefined(
      providerError?.code,
      responseData?.code,
      responseData?.errorCode,
      responseData?.data?.code,
      null,
    ),
  )
  const explicitRetryable = normalizeRetryableFlag(
    firstDefined(responseData?.data?.retryable, responseData?.retryable, providerError?.retryable),
    null,
  )
  const explicitRetryAfterSeconds = normalizeRetryAfterSeconds(
    firstDefined(
      responseData?.data?.retryAfterSeconds,
      responseData?.retryAfterSeconds,
      responseData?.data?.retryAfter,
      responseData?.retryAfter,
    ),
    null,
  )
  const isConfigMissing =
    explicitCode === 'EINVOICE_CONFIG_MISSING' || /unitCode is not configured/i.test(message || '')

  let normalizedCode = explicitCode || (isConfigMissing ? 'EINVOICE_CONFIG_MISSING' : null)
  let normalizedMessage = message || fallbackMessage
  let normalizedBlockerType = isConfigMissing ? 'config' : null
  let normalizedReasonCode = isConfigMissing ? 'CONFIG_THIEU_UNIT_CODE' : null
  let isStaleDraft = false
  let isRetryable = explicitRetryable === true
  let retryAfterSeconds = explicitRetryAfterSeconds
  const normalizedMessageText = String(normalizedMessage || '')

  const isDraftItemsEmpty =
    normalizedCode === 'DRAFT_ITEMS_EMPTY' ||
    (httpStatus === 409 &&
      (/\bDRAFT_ITEMS_EMPTY\b/i.test(normalizedMessageText) ||
        /draft.*(line[\s-_]?items?|item).*(empty|trống)/i.test(normalizedMessageText)))
  if (isDraftItemsEmpty) {
    normalizedCode = 'DRAFT_ITEMS_EMPTY'
    normalizedMessage =
      'Bản nháp chưa có line-items đã lưu. Vui lòng Re-bootstrap draft rồi issue lại.'
    normalizedBlockerType = 'draft_items_empty'
    normalizedReasonCode = 'DRAFT_ITEMS_EMPTY'
  }

  const isStaleDraftNotFound =
    normalizedCode === 'DRAFT_RECORD_NOT_FOUND' ||
    (httpStatus === 404 && /draft record not found/i.test(normalizedMessageText))
  if (isStaleDraftNotFound) {
    normalizedCode = 'DRAFT_RECORD_NOT_FOUND'
    normalizedMessage =
      'Draft hiện tại không còn tồn tại (stale). Vui lòng tải lại dữ liệu và Re-bootstrap draft trước khi phát hành.'
    normalizedBlockerType = 'stale_draft'
    normalizedReasonCode = 'DRAFT_RECORD_NOT_FOUND'
    isStaleDraft = true
  }

  const isPdfNotReady =
    normalizedCode === 'EINVOICE_PDF_NOT_READY' ||
    (httpStatus === 503 &&
      (/EINVOICE_PDF_NOT_READY/i.test(normalizedMessageText) ||
        /pdf.*not ready/i.test(normalizedMessageText)))
  if (isPdfNotReady) {
    normalizedCode = 'EINVOICE_PDF_NOT_READY'
    normalizedReasonCode = 'EINVOICE_PDF_NOT_READY'
    normalizedBlockerType = 'pdf_not_ready'
    isRetryable = explicitRetryable !== null ? explicitRetryable : true
    retryAfterSeconds = normalizeRetryAfterSeconds(retryAfterSeconds, 5)

    if (!normalizedMessage || normalizedMessage === fallbackMessage) {
      normalizedMessage = 'PDF chưa sẵn sàng, hệ thống sẽ thử lại sau ít giây.'
    }
  }

  const isContractGone = normalizedCode === 'CONTRACT_GONE' || httpStatus === 410
  if (isContractGone) {
    const deprecatedRoute =
      typeof responseData?.data?.deprecatedRoute === 'string'
        ? responseData.data.deprecatedRoute.trim()
        : ''
    const successors = Array.isArray(responseData?.data?.successors)
      ? responseData.data.successors
          .map((route) => (typeof route === 'string' ? route.trim() : ''))
          .filter(Boolean)
      : []

    normalizedCode = 'CONTRACT_GONE'
    normalizedMessage = [
      deprecatedRoute
        ? `Contract route cũ đã bị backend hard-cut (${deprecatedRoute}).`
        : 'Contract route cũ đã bị backend hard-cut.',
      'Vui lòng refresh để FE dùng endpoint canonical mới.',
      successors.length ? `Route thay thế: ${successors.join(', ')}.` : '',
    ]
      .filter(Boolean)
      .join(' ')
    normalizedBlockerType = 'contract_gone'
    normalizedReasonCode = 'CONTRACT_GONE'
    isRetryable = false
    retryAfterSeconds = null
  }

  const isSnapshotMissing =
    normalizedCode === 'EINVOICE_SNAPSHOT_MISSING' ||
    (httpStatus === 409 &&
      (/EINVOICE_SNAPSHOT_MISSING/i.test(normalizedMessageText) ||
        /snapshot.*missing/i.test(normalizedMessageText)))
  if (isSnapshotMissing) {
    normalizedCode = 'EINVOICE_SNAPSHOT_MISSING'
    normalizedMessage =
      'Canonical snapshot HĐĐT chưa sẵn sàng. Backend cần backfill dữ liệu trước khi FE có thể tiếp tục thao tác này.'
    normalizedBlockerType = 'snapshot_missing'
    normalizedReasonCode = 'EINVOICE_SNAPSHOT_MISSING'
    isRetryable = false
    retryAfterSeconds = null
  }

  const isSnapshotRepairRequired =
    normalizedCode === 'EINVOICE_SNAPSHOT_REPAIR_REQUIRED' ||
    (httpStatus === 409 &&
      (/EINVOICE_SNAPSHOT_REPAIR_REQUIRED/i.test(normalizedMessageText) ||
        /snapshot.*repair/i.test(normalizedMessageText)))
  if (isSnapshotRepairRequired) {
    normalizedCode = 'EINVOICE_SNAPSHOT_REPAIR_REQUIRED'
    normalizedMessage =
      'Canonical snapshot HĐĐT đang ở trạng thái repair_required. FE phải block thao tác và chờ backend repair/backfill.'
    normalizedBlockerType = 'snapshot_repair_required'
    normalizedReasonCode = 'EINVOICE_SNAPSHOT_REPAIR_REQUIRED'
    isRetryable = false
    retryAfterSeconds = null
  }

  const isBillModeUnsupported =
    normalizedCode === 'BILL_MODE_UNSUPPORTED' ||
    (httpStatus === 422 &&
      (/BILL_MODE_UNSUPPORTED/i.test(normalizedMessageText) ||
        /bill.?mode.*(unsupported|not.?supported)/i.test(normalizedMessageText)))
  if (isBillModeUnsupported) {
    normalizedCode = 'BILL_MODE_UNSUPPORTED'
    normalizedMessage =
      'Kênh nguồn của hóa đơn giao hàng chưa được hỗ trợ để phát hành HĐĐT. Cần normalize Bill.mode hoặc bổ sung policy backend.'
    normalizedBlockerType = 'bill_mode_unsupported'
    normalizedReasonCode = 'BILL_MODE_UNSUPPORTED'
    isRetryable = false
    retryAfterSeconds = null
  }

  if (
    !normalizedCode &&
    httpStatus === 400 &&
    /duplicate.*billids?|billids?.*trùng/i.test(normalizedMessageText)
  ) {
    normalizedCode = 'DRAFT_BATCH_DUPLICATE_BILL_IDS'
    normalizedMessage = 'Danh sách billIds có phần tử trùng. Vui lòng bỏ trùng trước khi gửi.'
  }

  if (
    !normalizedCode &&
    httpStatus === 400 &&
    /overrides?.*(billids?|salesinvoiceid)|billids?.*overrides?/i.test(normalizedMessageText)
  ) {
    normalizedCode = 'DRAFT_BATCH_OVERRIDE_OUTSIDE_BILL_IDS'
    normalizedMessage = 'overrides chứa billId không nằm trong billIds đã chọn.'
  }

  if (
    !normalizedCode &&
    httpStatus === 400 &&
    /extra top-level|additional properties|unknown field|strict schema/i.test(normalizedMessageText)
  ) {
    normalizedCode = 'DRAFT_STRICT_SCHEMA_INVALID'
    normalizedMessage = 'Payload gửi lên không đúng schema strict của backend.'
  }

  const previewBusinessMessage = PREVIEW_BUSINESS_ERROR_MESSAGES[normalizeErrorCode(normalizedCode)]
  if (previewBusinessMessage) {
    normalizedMessage = previewBusinessMessage
    normalizedReasonCode = normalizeErrorCode(normalizedCode)
  }

  const actionError = new Error(normalizedMessage || fallbackMessage)
  actionError.originalError = err
  actionError.response = err?.response
  actionError.provider = firstDefined(providerError?.provider, null)
  actionError.code = normalizedCode
  actionError.isConfigMissing = isConfigMissing
  actionError.einvoiceBlockerType = normalizedBlockerType
  actionError.reasonCode = normalizedReasonCode
  actionError.fastErrorInfo = fastErrorInfo
  actionError.fastErrorCategory = firstDefined(fastErrorInfo?.category, null)
  actionError.fastErrorMessage = providerMessage || null
  actionError.fastErrorNote = providerNote || null
  actionError.httpStatus = httpStatus
  actionError.isStaleDraft = isStaleDraft
  actionError.isRetryable = Boolean(isRetryable)
  actionError.retryAfterSeconds = retryAfterSeconds
  return actionError
}

export function createBulkIssuePrecheckError(err) {
  const status = err?.response?.status || null
  const fallbackMessageByStatus = {
    400: 'Payload kiểm tra trước phát hành không hợp lệ. Vui lòng kiểm tra lại thao tác và thử lại.',
    403: 'Bạn không có quyền kiểm tra trước phát hành cho công ty hiện tại.',
    404: 'Endpoint kiểm tra trước phát hành không còn hợp lệ. FE đang dùng route cũ hoặc backend chưa cập nhật đúng.',
    429: 'Hệ thống đang giới hạn tần suất kiểm tra trước phát hành. Vui lòng thử lại sau.',
  }
  const normalizedError = createEInvoiceActionError(
    err,
    fallbackMessageByStatus[status] || 'Kiểm tra điều kiện phát hành thất bại',
  )

  if (fallbackMessageByStatus[status]) {
    normalizedError.message = fallbackMessageByStatus[status]
  }

  normalizedError.httpStatus = status
  return normalizedError
}

export function createBulkIssueSubmitError(err) {
  const status = err?.response?.status || null
  const fallbackMessageByStatus = {
    400: 'Payload phát hành hàng loạt không hợp lệ. Vui lòng kiểm tra lại dữ liệu gửi đi.',
    403: 'Không thể phát hành vì company hiện tại không khớp với tenant đăng nhập.',
    409: 'Idempotency-Key đang dùng cho payload khác. Hãy thử lại để hệ thống cấp khóa mới.',
  }
  const normalizedError = createEInvoiceActionError(
    err,
    fallbackMessageByStatus[status] || 'Phát hành hàng loạt thất bại',
  )

  if (fallbackMessageByStatus[status]) {
    normalizedError.message = fallbackMessageByStatus[status]
  }

  normalizedError.httpStatus = status
  return normalizedError
}

export function createBulkIssueProgressError(err) {
  const status = err?.response?.status || null
  const defaultMessage = 'Không thể lấy tiến độ phát hành hàng loạt'
  const fallbackMessageByStatus = {
    404: 'Không tìm thấy job phát hành (không tồn tại, hết hạn hoặc không thuộc company hiện tại).',
  }
  const normalizedError = createEInvoiceActionError(
    err,
    fallbackMessageByStatus[status] || defaultMessage,
  )

  if (fallbackMessageByStatus[status]) {
    normalizedError.message = fallbackMessageByStatus[status]
  } else {
    normalizedError.message = defaultMessage
  }

  normalizedError.httpStatus = status
  normalizedError.isRetryable = status !== 404
  return normalizedError
}

export function createDeleteDraftError(err) {
  const status = err?.response?.status || null
  const fallbackMessageByStatus = {
    400: 'Bản nháp đã bị xóa trước đó. Vui lòng tải lại danh sách.',
    404: 'Không tìm thấy bản nháp. Có thể đã bị xóa bởi thao tác khác.',
    409: 'Thao tác xóa nháp không được phép ở trạng thái hiện tại.',
  }
  const normalizedError = createEInvoiceActionError(
    err,
    fallbackMessageByStatus[status] || 'Xoá bản nháp thất bại',
  )

  if (fallbackMessageByStatus[status]) {
    normalizedError.message = fallbackMessageByStatus[status]
  }

  normalizedError.httpStatus = status
  return normalizedError
}
